import type { CreateBrandData, Brand, BrandQueryParams, BrandStats, UpdateBrandData } from 'shared/types';
import api from '../axios';
 

// Brand Service Class
export class BrandService {
  // Brand CRUD Operations
  async createBrand(data: CreateBrandData): Promise<Brand> {
    const response = await api.post<Brand>('/brands', data);
    return response.data;
  }

  async getBrands(params?: BrandQueryParams): Promise<Brand[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());
    if (params?.includeRelations) queryParams.append('includeRelations', 'true');
    if (params?.activeOnly) queryParams.append('activeOnly', 'true');

    const url = `/brands${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<Brand[]>(url);
    return response.data;
  }

  async getBrandById(id: string, includeRelations = false): Promise<Brand> {
    const url = `/brands/${id}${includeRelations ? '?includeRelations=true' : ''}`;
    const response = await api.get<Brand>(url);
    return response.data;
  }

  async getBrandStats(id: string): Promise<BrandStats> {
    const response = await api.get<BrandStats>(`/brands/${id}/stats`);
    return response.data;
  }

  async updateBrand(id: string, data: UpdateBrandData): Promise<Brand> {
    const response = await api.patch<Brand>(`/brands/${id}`, data);
    return response.data;
  }

  async activateBrand(id: string): Promise<Brand> {
    const response = await api.patch<Brand>(`/brands/${id}/activate`);
    return response.data;
  }

  async deactivateBrand(id: string): Promise<Brand> {
    const response = await api.patch<Brand>(`/brands/${id}/deactivate`);
    return response.data;
  }

  async deleteBrand(id: string): Promise<void> {
    await api.delete(`/brands/${id}`);
  }

  // Utility Methods
  async getActiveBrands(includeRelations = false): Promise<Brand[]> {
    return this.getBrands({ activeOnly: true, includeRelations });
  }

  async searchBrands(searchTerm: string): Promise<Brand[]> {
    // This would depend on backend search implementation
    console.log(`Searching for brands with term: ${searchTerm}`);
    return this.getBrands();
  }
}

// Create and export singleton instance
export const brandService = new BrandService();

// Export individual methods for convenience
export const {
  createBrand,
  getBrands,
  getBrandById,
  getBrandStats,
  updateBrand,
  activateBrand,
  deactivateBrand,
  deleteBrand,
  getActiveBrands,
  searchBrands
} = brandService;